/**
 * Firebase Auth, loaded on demand.
 *
 * The API can run in two modes. Under `dev` it signs its own JWT and Firebase is never
 * touched — so the SDK is behind dynamic imports and stays out of the initial bundle
 * unless production auth is actually in use.
 *
 * Under `firebase`, sign-in is a two-step exchange: the API mints a **custom token**
 * after verifying the e-mailed code, and this module trades it for an **ID token**,
 * which the API then verifies with the Admin SDK on every request. The SDK owns
 * refreshing that ID token from then on, which is why `getFreshIdToken` asks it for
 * the current value rather than caching one.
 */

import type { Auth } from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let authPromise: Promise<Auth> | null = null;

/** True when the build carries enough configuration to talk to Firebase. */
export function isFirebaseConfigured(): boolean {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);
}

async function getAuth(): Promise<Auth> {
  if (!isFirebaseConfigured()) {
    throw new Error(
      "Firebase is not configured. Set VITE_FIREBASE_* for this build.",
    );
  }
  authPromise ??= (async () => {
    const [
      { initializeApp, getApps, getApp },
      { initializeAuth, indexedDBLocalPersistence, browserLocalPersistence },
    ] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
    const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    // Not `getAuth()`: its full initialisation includes the popup/redirect
    // resolver, which never settles inside Capacitor's WKWebView (capacitor://
    // scheme), hanging sign-in and boot forever. This app only signs in with a
    // custom token, so the resolver is dead weight even on the web.
    return initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
    });
  })();
  return authPromise;
}

/** Exchange the API's custom token for a Firebase ID token. */
export async function exchangeCustomToken(
  customToken: string,
): Promise<string> {
  const auth = await getAuth();
  const { signInWithCustomToken } = await import("firebase/auth");
  const credential = await signInWithCustomToken(auth, customToken);
  return credential.user.getIdToken();
}

/**
 * Resolve once the SDK has finished restoring a persisted session.
 *
 * `auth.currentUser` is null for the first moments after start-up even when a session
 * exists, so reading it directly on a cold start reports "signed out" and the app
 * would drop a perfectly good session.
 */
async function waitForAuthReady(auth: Auth): Promise<void> {
  const { onAuthStateChanged } = await import("firebase/auth");
  await new Promise<void>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
  });
}

/**
 * The current ID token, refreshed by the SDK when it is close to expiring.
 * Returns null only when nobody is actually signed in.
 */
export async function getFreshIdToken(): Promise<string | null> {
  const auth = await getAuth();
  await waitForAuthReady(auth);
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export async function firebaseSignOut(): Promise<void> {
  if (!isFirebaseConfigured()) return;
  const auth = await getAuth();
  const { signOut } = await import("firebase/auth");
  await signOut(auth);
}
